import type { Coords } from 'google-map-react';
import { t } from 'i18next';
import { makeAutoObservable } from 'mobx';

import { BIRequests } from '~/api/BI';
import type { AutocompleteSuggestion } from '~/api/DeliveryAddress';
import { DeliveryMethods, type ETACalculation } from '~/api/ETA';
import type { PartnerPickupPoint } from '~/api/PartnerPickupPoints';
import type { PickupPoint } from '~/api/PickupPoints';
import { company } from '~/company/Company';
import type { DeliveryZoneRef } from '~/components/ModalDeliveryAddress/GMap/interfaces';
import {
  isInsideZone,
  isSameCoordinate,
} from '~/components/ModalDeliveryAddress/GMap/utils';
import { mainStore, orderStore, userStore } from '~/stores';
import type { DeliveryAddress } from '~/stores/UserStore';

import type { EditAddressData } from './interface';

/**
 * Fulfilment type the address modal is currently collecting. Both types are
 * ordered as a courier delivery — the backend derives the actual type from the
 * delivery zone the address falls into (`displayType`).
 */
export type DeliveryType = 'courier' | 'pudo';

interface OutOfZoneInterest {
  coordinates: {
    lat: number;
    lng: number;
  };
  phone: string;
  email: string;
  name?: string;
  deviceId?: string;
  userId?: string;
}

export const {
  map: { center: mapCenter, zoom: mapZoom },
} = company.config;

const PARTNER_PICKUP_FALLBACK_ZIP = '000000';

class DeliveryAddressStore {
  isMapBusy = true;
  mapCenter: GeoCoordinates = mapCenter;
  selectedZone: Nullable<DeliveryZoneRef> = null;
  deliveryType: DeliveryType = 'courier';
  selectedPickupPoint: Nullable<PickupPoint> = null;
  /**
   * When true, zone matching (getSelectedPolygon) ignores click-and-collect
   * (pickup) zones so an address is only ever bound to a courier-delivery zone.
   * Set by the delivery/pickup modal in Delivery mode; default false keeps the
   * legacy behaviour for every other flow.
   */
  excludePickupZones = false;
  mapZoom = mapZoom;
  geolocationCoordinates: GeoCoordinates | null = null;
  curGeolocationAddress: AutocompleteSuggestion | null = null;
  isActiveGeolocation = false;
  /**
   * The customer typed the house number themselves, so reverse geocoding must
   * leave that field alone — including when they cleared it on a tenant where
   * a house number is optional.
   *
   * Cleared by everything that means "I am picking a different house": moving
   * the map center (setMapCenter, which covers a pin drag, geolocation and a
   * picked suggestion alike), switching the fulfilment type, and resetStore
   * when the modal closes. A zoom does not clear it — the pin has not moved.
   */
  isManuallyInput = false;
  /**
   * Point the house number currently in the form was resolved for.
   *
   * Reverse geocoding fills that field, and re-resolving the same point must
   * not touch it again — that is what made a saved house number flicker to the
   * geocoder's reading when an edit modal reopened. The address's own
   * coordinates cannot answer this: the address search row writes the picked
   * point into `deliveryAddress` before asking for the geocode, which would
   * make every pick look like a re-resolve of what the form already held.
   */
  buildingResolvedAt: Nullable<GeoCoordinates> = null;
  /**
   * The house number in the form is the geocoder's own reading rather than
   * the customer's input or a saved address.
   *
   * Only such a number gives way when the pin moves onto a spot the geocoder
   * has no number for — it belonged to the previous house. A number the
   * customer typed or saved stays: an empty answer says nothing about it.
   */
  isBuildingGeocoded = false;
  isLoading = false;
  isAwaitGeolocation = false;
  isAwaitSubscription = false;
  inputAddressValue = '';
  inputAddressFocused = false;
  deliveryAddress: DeliveryAddress | null = null;
  etaCalculation: ETACalculation | null = null;
  isAddressNotExist = false;
  isAddressNotCovered = false;
  isSmallSizeCover = true;
  nameVal = '';
  emailVal = '';
  isShowAddressNotFound = false;
  isShowAddressFound = false;
  isPushRequested = false;
  editAddressData: EditAddressData = {
    addressId: null,
    address2: '',
    addressBld: '',
    addressFloor: '',
    addressApt: '',
    type: 'home',
    comment: '',
    instructions: [],
  };
  isChangeDeliveryAreaPopover = false;
  /**
   * Map center the modal positioned itself, for which automatic reverse
   * geocoding must not run. Opening the "add a new address" flow points the
   * map at the customer's current neighbourhood purely as context; resolving
   * an address for that point would fill the search field with the address
   * they are trying to replace.
   *
   * Scoped to one coordinate rather than held as an "until the user acts"
   * flag: every real interaction — dragging the pin, geolocation, picking a
   * suggestion — moves the center elsewhere and resolves as usual, so no
   * caller has to remember to clear this.
   */
  autoResolveSuppressedAt: Nullable<GeoCoordinates> = null;
  renderedZonesOnMap: Record<string, DeliveryZoneRef> = {};
  isOutOfZoneModalOpen = false;
  isShowOutOfZoneInline = false;
  outOfZonePhone = '';
  outOfZoneEmail = '';
  outOfZoneName = '';
  outOfZoneCoordinates: GeoCoordinates | null = null;

  private _geocodingAbortController: AbortController | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  get isEmailValid(): boolean {
    return mainStore.validateEmail(this.emailVal);
  }

  get isFormValid(): boolean {
    return this.isEmailValid && !!this.nameVal.length;
  }

  /**
   * Whether the map currently collects a PUDO point instead of a street
   * address: either the user picked that type in the switcher, or the company
   * has no courier delivery at all.
   */
  get isPudoMode(): boolean {
    return company.hasDeliveryTypeSwitcher
      ? this.deliveryType === 'pudo'
      : company.isPudoOnly;
  }

  /** Whether the map sits on the point {@link autoResolveSuppressedAt} marks. */
  get isAutoResolveSuppressed(): boolean {
    const point = this.autoResolveSuppressedAt;

    return !!point && isSameCoordinate(point, this.mapCenter);
  }

  setDeliveryType(val: DeliveryType) {
    this.deliveryType = val;
  }

  /**
   * Switches the fulfilment type the modal collects, dropping every artefact of
   * the type being left: its address, zone, pickup point, ETA and form fields.
   * The map returns to the company's default view, which the geocoding effect
   * reads as "nothing picked yet" — so the next choice starts from scratch and
   * nothing is carried over or restored.
   *
   * Re-resolving the point the customer was standing on is what made a courier
   * address come back as the building housing the pickup point they had just
   * left (JS-14451), so no address is derived here. Coverage still is: the
   * geocoding effect skips only the reverse-geocoding for a default center, and
   * goes on to ask for an ETA there — the same request a modal opened by a
   * customer with no saved address already makes.
   */
  switchDeliveryType(next: DeliveryType) {
    this._geocodingAbortController?.abort();
    this._geocodingAbortController = null;
    this.isLoading = false;

    this.deliveryType = next;
    this.selectedZone = null;
    this.selectedPickupPoint = null;
    this.deliveryAddress = null;
    this.etaCalculation = null;
    this.inputAddressValue = '';
    this.isAddressNotCovered = false;
    this.isAddressNotExist = false;
    this.isShowOutOfZoneInline = false;
    // The building number is the one field that outlives its address by
    // design (see setDeliveryAddress). Left alone, a pickup point's
    // `buildingFallback` — literally "ПВЗ" — would follow the customer onto
    // the courier address they pick next. addressId is deliberately kept:
    // it identifies the address being edited, and clearing it would turn an
    // edit into a new address.
    //
    // Clearing the field also hands it back to the geocoder: the number the
    // customer typed belonged to the fulfilment type they just left, and a
    // pickup point needs its own `buildingFallback` to be written.
    this.isManuallyInput = false;
    this.buildingResolvedAt = null;
    this.isBuildingGeocoded = false;
    this.autoResolveSuppressedAt = null;
    this.editAddressData = {
      ...this.editAddressData,
      address2: '',
      addressBld: '',
      addressFloor: '',
      addressApt: '',
      comment: '',
      instructions: [],
    };
    this.resetOutOfZoneForm();
    // Both maps center on the geolocation coordinates while this flag is up and
    // ignore their own change events, so leaving it set would park the map on
    // the customer's position while the store believed it sat on the default
    // view — and the coverage request below would be made for a point nobody
    // can see.
    this.isActiveGeolocation = false;
    this.geolocationCoordinates = null;
    this.mapCenter = company.config.map.center;
    this.mapZoom = company.config.map.zoom;
  }

  setAddressType(type: string) {
    if (type === 'home') {
      this.setAddressTypeValue(type);
    } else {
      this.setAddressTypeValue('');
    }
  }

  setAddressTypeValue(val: string) {
    this.editAddressData.type = val;
  }

  setIsMapBusy(flag: boolean) {
    this.isMapBusy = flag;
  }

  setIsActiveGeolocation(flag: boolean) {
    this.isActiveGeolocation = flag;
  }

  setIsLoading(flag: boolean) {
    this.isLoading = flag;
  }

  setInputAddressFocused(flag: boolean) {
    this.inputAddressFocused = flag;
  }

  setIsAddressNotExist(flag: boolean) {
    this.isAddressNotExist = flag;
  }

  setIsAddressNotCovered(flag: boolean) {
    this.isAddressNotCovered = flag;
  }

  setMapCenter(val: GeoCoordinates) {
    const hasMoved = !isSameCoordinate(this.mapCenter, val);
    this.mapCenter = val;

    if (!hasMoved) return;

    // Another spot means another house: the geocoder gets the field back.
    this.isManuallyInput = false;

    // Moving off the suppressed point ends the suppression for good, so
    // landing back on the very same coordinate later still resolves.
    if (this.autoResolveSuppressedAt && !this.isAutoResolveSuppressed) {
      this.autoResolveSuppressedAt = null;
    }
  }

  setAutoResolveSuppressedAt(point: Nullable<GeoCoordinates>) {
    this.autoResolveSuppressedAt = point;
  }

  setSelectedZone(zone: Nullable<DeliveryZoneRef>) {
    this.selectedZone = zone;
  }

  setSelectedPickupPoint(point: Nullable<PickupPoint>) {
    this.selectedPickupPoint = point;
  }

  /**
   * Selects an external pickup point (ПВЗ): stores it and derives the delivery
   * address directly from the point's data — no reverse-geocoding required.
   */
  selectPickupPoint(point: PickupPoint) {
    this.selectedPickupPoint = point;
    // Not left to setMapCenter: the map is often already centered on the point
    // by the time this runs, and then the center never "moves".
    this.isManuallyInput = false;
    this.buildingResolvedAt = null;
    this.isBuildingGeocoded = false;
    this.setMapCenter({ lat: point.latitude, lng: point.longitude });
    this.setDeliveryAddress({
      zip: point.attributes.address?.zipCode || '',
      country: 'RU',
      city: point.city,
      region: point.attributes.address?.region || '',
      address1: point.fullAddress,
      address2: '',
      addressFloor: '',
      addressBld: point.attributes.address?.house || '',
      addressApt: '',
      shortAddress: point.attributes.shortAddress || point.fullAddress,
      coordinates: { lat: point.latitude, lng: point.longitude },
      placeId: mainStore.getPseudoId(),
      addressId: null,
      comment: '',
      instructions: [],
      type: 'home',
      pickupPointId: point.id,
    });
  }

  /**
   * Selects a partner (EMU) pickup point. The point is tariffed by the courier
   * zone its coordinates fall into, so the ETA is asked for them as
   * `partner_pup` first. Resolves to false when no zone covers the point: such
   * a point cannot be ordered to and nothing is applied.
   */
  async selectPartnerPickupPoint(point: PartnerPickupPoint): Promise<boolean> {
    const coordinates = {
      lat: point.location.latitude,
      lng: point.location.longitude,
    };

    const [etaCalculation, geocodedAddress] = await Promise.all([
      orderStore
        .fetchETA(`${coordinates.lat},${coordinates.lng}`, undefined, {
          destinationKind: 'partner_pup',
        })
        .catch(() => null),
      userStore.geocodingByCoordinates(coordinates).catch(() => null),
    ]);

    if (!etaCalculation) {
      mainStore.pushAlert('error', t('addressOutOfArea'));
      return false;
    }

    this.setSelectedZone(null);
    this.setEtaCalculation(etaCalculation);
    this.setIsAddressNotCovered(false);
    this.setIsAddressNotExist(false);
    this.setMapCenter(coordinates);
    this.setDeliveryAddress({
      zip: geocodedAddress?.zip || PARTNER_PICKUP_FALLBACK_ZIP,
      country: company.config.countryCode || geocodedAddress?.country || '',
      city: point.city?.name || geocodedAddress?.city || '',
      region: point.region?.name || geocodedAddress?.region || '',
      address1: point.address,
      address2: '',
      addressFloor: '',
      addressBld: t('pudo'),
      addressApt: '',
      shortAddress: point.name,
      coordinates,
      placeId: mainStore.getPseudoId(),
      addressId: null,
      comment: '',
      instructions: [],
      type: 'home',
      pickupPointId: point.id,
      partnerPupExternalId: point.external_code,
    });

    return true;
  }

  setExcludePickupZones(val: boolean) {
    this.excludePickupZones = val;
  }

  selectZoneByCoordinates(coords: GeoCoordinates) {
    const zone = this.getSelectedPolygon(coords);
    this.setMapCenter(coords);
    this.setSelectedZone(zone);
  }

  setMapZoom(val: number) {
    this.mapZoom = val;
  }

  setInputAddressValue(val: string) {
    this.inputAddressValue = val;
  }

  setDeliveryAddress(val: DeliveryAddress | null) {
    if (!val) {
      this.deliveryAddress = val;
      return;
    }

    const editData = this.editAddressData;
    // The new point's own house number wins: form data outlives the address it
    // was typed for, so the previous building would follow the customer onto
    // every address picked after it.
    this.deliveryAddress = Object.assign(val, {
      ...editData,
      addressBld: val.addressBld || editData.addressBld,
    });
  }

  setDeliveryAddress2(val: string) {
    if (this.editAddressData) {
      this.editAddressData.address2 = val;
    }
  }

  setDeliveryAddressFloor(val: string) {
    if (this.editAddressData && val.length < 51) {
      this.editAddressData.addressFloor = val;
    }
  }

  setDeliveryAddressBld(val: string) {
    if (this.editAddressData && val.length < 51) {
      this.editAddressData.addressBld = val;
    }
  }

  /**
   * House number as typed by the customer. Every building input goes through
   * here rather than {@link setDeliveryAddressBld}, which reverse geocoding
   * also uses: what marks the value as the customer's own is the input, not
   * the field being written.
   */
  setDeliveryAddressBldManually(val: string) {
    const before = this.editAddressData.addressBld;

    this.setDeliveryAddressBld(val);

    if (this.editAddressData.addressBld === before) return;

    this.isManuallyInput = true;
    // The field no longer holds what the geocoder resolved, so the point it
    // was resolved for says nothing about it: without this, returning the pin
    // to that point would count as "already resolved here" and the customer's
    // number would survive a move that is supposed to overwrite it.
    this.buildingResolvedAt = null;
    this.isBuildingGeocoded = false;
  }

  /**
   * Hands the house number over to the next reverse geocoding, whatever it
   * answers — an empty answer included.
   *
   * For a point that is not the customer's house at all, such as a store
   * picked for pickup: the number in the form belongs to their delivery
   * address and must not follow them onto the store's. Reopening a saved
   * address on its own point keeps the saved number.
   */
  releaseBuildingToGeocoder(point: GeoCoordinates) {
    if (
      this.buildingResolvedAt &&
      isSameCoordinate(this.buildingResolvedAt, point)
    ) {
      return;
    }

    this.isManuallyInput = false;
    this.buildingResolvedAt = null;
    this.isBuildingGeocoded = true;
  }

  setDeliveryAddressApt(val: string) {
    if (this.editAddressData && val.length < 51) {
      this.editAddressData.addressApt = val;
    }
  }

  setDeliveryAddressComment(val: string) {
    if (this.editAddressData && val.length < 128) {
      this.editAddressData.comment = val;
    }
  }

  addInstruction(val: string) {
    this.editAddressData.instructions.push(val);
  }

  deleteInstruction(val: string) {
    this.editAddressData.instructions =
      this.editAddressData.instructions.filter(
        (instruction) => instruction !== val,
      );
  }

  resetInstructionList() {
    this.editAddressData.instructions = [];
  }

  setEtaCalculation(val: ETACalculation | null) {
    this.etaCalculation = val;
  }

  setIsSmallSizeCover(flag: boolean) {
    this.isSmallSizeCover = flag;
  }

  setNameVal(val: string) {
    this.nameVal = val;
  }

  setEmailVal(val: string) {
    this.emailVal = val;
  }

  setIsShowAddressNotFound(flag: boolean) {
    this.isShowAddressNotFound = flag;
  }

  setIsShowAddressFound(flag: boolean) {
    this.isShowAddressFound = flag;
  }

  setIsAwaitGeolocation(flag: boolean) {
    this.isAwaitGeolocation = flag;
  }

  setIsAwaitSubscription(flag: boolean) {
    this.isAwaitSubscription = flag;
  }

  setGeolocationCoordinates(val: GeoCoordinates | null) {
    this.geolocationCoordinates = val;

    /**
     * Code below sets new center of map, together with "geolocationCoordinates"
     *
     * TODO: Not sure why this properties are different. Needs to remove one of them
     * */
    if (val) {
      // Through the setter, so locating the customer clears
      // autoResolveSuppressedAt like every other move of the center.
      this.setMapCenter(val);
    }
  }

  setCurGeolocationAddress(val: AutocompleteSuggestion | null) {
    this.curGeolocationAddress = val;
  }

  setIsPushRequested(flag: boolean) {
    this.isPushRequested = flag;
  }

  /**
   * Fills the details form.
   *
   * `resolvedAt` is the point whose house number the form now holds, so
   * re-resolving that same point leaves the field alone. Callers opening a
   * blank form pass nothing: the geocoder is then free to fill it in. It is a
   * parameter rather than something read off `deliveryAddress` because the
   * call sites do not agree on the order — the legacy modal fills the form
   * before it has an address — and a marker derived from whatever the store
   * happened to hold was silently wrong there.
   */
  setEditAddressData(val: EditAddressData, resolvedAt?: GeoCoordinates) {
    this.editAddressData = val;
    this.buildingResolvedAt = resolvedAt
      ? { lat: resolvedAt.lat, lng: resolvedAt.lng }
      : null;
    this.isBuildingGeocoded = false;
  }

  setIsChangeDeliveryAreaPopover(flag: boolean) {
    this.isChangeDeliveryAreaPopover = flag;
  }

  setIsOutOfZoneModalOpen(isOpen: boolean) {
    this.isOutOfZoneModalOpen = isOpen;
  }

  setIsShowOutOfZoneInline(isShow: boolean) {
    this.isShowOutOfZoneInline = isShow;
  }

  setOutOfZonePhone(phone: string) {
    this.outOfZonePhone = phone;
  }

  setOutOfZoneEmail(email: string) {
    this.outOfZoneEmail = email;
  }

  setOutOfZoneName(name: string) {
    this.outOfZoneName = name;
  }

  setOutOfZoneCoordinates(coordinates: GeoCoordinates | null) {
    this.outOfZoneCoordinates = coordinates;
  }

  resetOutOfZoneForm() {
    this.outOfZonePhone = '';
    this.outOfZoneEmail = '';
    this.outOfZoneName = '';
    this.outOfZoneCoordinates = null;
    this.setIsOutOfZoneModalOpen(false);
  }

  async submitOutOfZoneInterest() {
    if (!this.outOfZoneCoordinates) {
      return;
    }

    const interestData: OutOfZoneInterest = {
      coordinates: this.outOfZoneCoordinates,
      phone: this.outOfZonePhone,
      email: this.outOfZoneEmail,
      name: this.outOfZoneName || undefined,
      deviceId: userStore.deviceId,
      userId: userStore.personalData.id || undefined,
    };

    try {
      await BIRequests.sendAnalytics('out-of-zone-interest', interestData);
      mainStore.pushAlert('success', t('outOfZone:thankYou'));
      deliveryAddressStore.setIsOutOfZoneModalOpen(false);
      this.resetOutOfZoneForm();
    } catch {
      mainStore.pushAlert('error', t('errors:somethingWentWrong'));
    }
  }

  resetStore() {
    this._geocodingAbortController?.abort();
    this._geocodingAbortController = null;
    this.isMapBusy = true;
    this.mapCenter = company.config.map.center;
    this.autoResolveSuppressedAt = null;
    this.buildingResolvedAt = null;
    this.isBuildingGeocoded = false;
    this.geolocationCoordinates = null;
    this.isActiveGeolocation = false;
    this.isManuallyInput = false;
    this.isLoading = false;
    this.isAwaitGeolocation = false;
    this.isAwaitSubscription = false;
    this.inputAddressValue = '';
    this.inputAddressFocused = false;
    this.deliveryAddress = null;
    this.etaCalculation = null;
    this.isAddressNotExist = false;
    this.isAddressNotCovered = false;
    this.isSmallSizeCover = true;
    this.nameVal = '';
    this.emailVal = '';
    this.isShowAddressNotFound = false;
    this.isShowAddressFound = false;
    this.isShowOutOfZoneInline = false;
    this.curGeolocationAddress = null;
    this.isPushRequested = false;
    this.selectedZone = null;
    this.editAddressData = {
      addressId: null,
      address2: '',
      addressFloor: '',
      addressBld: '',
      addressApt: '',
      type: 'home',
      comment: '',
      instructions: [],
    };
  }

  async geocoding(
    skipGeocoding = false,
    { lat, lng }: GeoCoordinates,
    skipSideEffects = false,
  ) {
    this._geocodingAbortController?.abort();
    const abortController = new AbortController();
    this._geocodingAbortController = abortController;

    this.setIsLoading(true);
    const deliveryMethod =
      this.getDeliveryMethodBySelectedZone() ??
      (company.showDeliveryMethodSwitcher && this.excludePickupZones
        ? DeliveryMethods.COURIER_DELIVERY
        : undefined);

    try {
      const etaCalculation = await orderStore
        .fetchETA(`${lat},${lng}`, deliveryMethod, {
          signal: abortController.signal,
          // The modal knows the type being picked right now — the saved
          // address still carries the previous one until this point is saved.
          destinationKind: this.isPudoMode ? 'pup' : 'customer',
          skipSideEffects,
        })
        .catch(() => null);

      if (abortController.signal.aborted) return;

      mainStore.sendToRN('sendTags', {
        not_in_the_area: 'false',
      });

      this.setEtaCalculation(etaCalculation);
      this.setIsAddressNotCovered(false);
      this.setIsAddressNotExist(false);

      if (!etaCalculation) {
        if (company.isEnabledOutOfZoneModal) {
          this.setOutOfZoneCoordinates({ lat, lng });
          this.setIsShowOutOfZoneInline(true);
        } else {
          mainStore.pushAlert('error', t('addressOutOfArea'));
        }

        mainStore.sendToRN('sendTags', {
          not_in_the_area: true,
        });

        this.setIsAddressNotCovered(true);
        this.setDeliveryAddress(null);
        this.setInputAddressValue('');
        return;
      }

      this.setIsShowOutOfZoneInline(false);

      mainStore.popAlert();

      if (!skipGeocoding) {
        const prevPlaceId = this.deliveryAddress?.placeId;
        try {
          const deliveryAddress = await userStore.geocodingByCoordinates(
            { lat, lng },
            // A pickup point has no house number of its own, so the PUDO label
            // stands in for it and keeps the address valid.
            { buildingFallback: this.isPudoMode ? t('pudo') : '' },
          );

          if (abortController.signal.aborted) return;

          // Moving to another point means another house: the form still holds
          // the building of the address it was opened for, and without this it
          // would be saved against the new one.
          //
          // Keyed on the point the field was filled for, not on placeId:
          // reverse geocoding mints a random placeId every time (getPseudoId),
          // so this used to fire on every resolve — including re-resolving the
          // very same spot when an edit modal opens, which is what made a saved
          // house number flip to whatever the geocoder reads off those
          // coordinates.
          //
          // A house number the customer typed is never overwritten either.
          // Their input outranks the geocoder until they pick another spot.
          //
          // Nor does a spot the geocoder has no number for (a street, a POI)
          // erase a number the customer typed or saved: an empty answer is not
          // a different house number. Only the geocoder's own reading for the
          // previous spot gives way, as it belonged to another house.
          const isAlreadyResolvedHere =
            !!this.buildingResolvedAt &&
            isSameCoordinate(this.buildingResolvedAt, { lat, lng });
          const geocodedBuilding = deliveryAddress?.addressBld || '';

          if (
            deliveryAddress &&
            !isAlreadyResolvedHere &&
            !this.isManuallyInput &&
            (geocodedBuilding || this.isBuildingGeocoded)
          ) {
            this.setDeliveryAddressBld(geocodedBuilding);
            this.buildingResolvedAt = { lat, lng };
            this.isBuildingGeocoded = true;
          }

          // The address carries the form's house number, not the geocoder's:
          // saving falls back to `deliveryAddress.addressBld` while the form is
          // untouched, and a geocoded number left here would replace a saved
          // empty one the customer never saw in the field.
          this.setDeliveryAddress(
            deliveryAddress && {
              ...deliveryAddress,
              addressBld: this.editAddressData.addressBld,
            },
          );
          this.setInputAddressValue(deliveryAddress?.address1 || '');
        } catch {
          if (abortController.signal.aborted) return;
          this.setDeliveryAddress(null);
          this.setInputAddressValue('');
        }
        if (prevPlaceId === this.deliveryAddress?.placeId) {
          if (!this.etaCalculation && !userStore.isFirstLaunch) {
            this.setIsAddressNotCovered(true);
          }
          return;
        }
      }
    } catch {
      if (abortController.signal.aborted) return;
      this.setEtaCalculation(null);
      mainStore.sendToRN('sendTags', {
        not_in_the_area: true,
      });

      if (!userStore.isFirstLaunch) {
        this.setIsAddressNotCovered(true);
      }
    } finally {
      if (!abortController.signal.aborted) {
        this.setIsLoading(false);
      }
    }
  }

  getDeliveryMethodBySelectedZone(): DeliveryMethods | undefined {
    if (!this.selectedZone) {
      return;
    }

    const { zone } = this.selectedZone;

    return zone.deliveryMethod.shippingType;
  }

  getSelectedPolygon(point: Coords) {
    const matchedZones: DeliveryZoneRef[] = [];

    for (const deliveryZoneRef of Object.values(this.renderedZonesOnMap)) {
      if (
        this.excludePickupZones &&
        deliveryZoneRef.zone.deliveryMethod.shippingType ===
          DeliveryMethods.CLICK_AND_COLLECT
      ) {
        continue;
      }

      // With the switcher on, a courier address must never bind to a PUDO zone
      // (and the other way round) — the zone type is what the backend reads
      // the fulfilment type from.
      if (
        company.hasDeliveryTypeSwitcher &&
        (deliveryZoneRef.zone.displayType === 'pudo') !== this.isPudoMode
      ) {
        continue;
      }

      const isSelectedPolygon = isInsideZone(point, deliveryZoneRef.polygon);

      if (!isSelectedPolygon) {
        continue;
      }

      matchedZones.push(deliveryZoneRef);
    }

    if (!matchedZones.length) {
      return null;
    }

    const sorted = matchedZones.sort(
      (a, b) => b.zone.priority - a.zone.priority,
    );
    const highestPriorityZone = sorted[0];

    return highestPriorityZone ?? null;
  }
}

export const deliveryAddressStore = new DeliveryAddressStore();
